import * as React from "react"
import { cn } from "@/lib/utils"

type CategoryEntry = { label: string; actions: Record<string, string> }
type Catalog = Record<string, CategoryEntry>

type Props = {
  catalog: Catalog
  selected: string[]
  onChange: (next: string[]) => void
  disabled?: boolean
}

// Native checkbox with a controlled indeterminate state (no DOM attribute for it).
function Checkbox({
  indeterminate = false,
  className,
  ...props
}: React.InputHTMLAttributes<HTMLInputElement> & { indeterminate?: boolean }) {
  const ref = React.useRef<HTMLInputElement>(null)
  React.useEffect(() => {
    if (ref.current) ref.current.indeterminate = indeterminate
  }, [indeterminate])
  return <input ref={ref} type="checkbox" className={cn("accent-primary shrink-0", className)} {...props} />
}

// GitHub-scopes layout: one bordered card, a section per category, every row
// a two-column line — checkbox + key on the left, description on the right.
export default function PermissionPicker({ catalog, selected, onChange, disabled }: Props) {
  const selectedSet = React.useMemo(() => new Set(selected), [selected])

  const toggle = (key: string) => {
    if (disabled) return
    const next = new Set(selectedSet)
    if (next.has(key)) next.delete(key)
    else next.add(key)
    onChange(Array.from(next))
  }

  const toggleGroup = (keys: string[]) => {
    if (disabled) return
    const allSelected = keys.every((k) => selectedSet.has(k))
    const next = new Set(selectedSet)
    keys.forEach((k) => (allSelected ? next.delete(k) : next.add(k)))
    onChange(Array.from(next))
  }

  return (
    <div className="divide-y rounded-lg border bg-card text-card-foreground">
      {Object.entries(catalog).map(([cat, { label, actions }]) => {
        const keys = Object.keys(actions).map((a) => `${cat}.${a}`)
        const checkedCount = keys.filter((k) => selectedSet.has(k)).length
        const allChecked = checkedCount === keys.length

        return (
          <section key={cat} className="px-4 py-3">
            <label className="flex cursor-pointer flex-col gap-1 py-1 sm:flex-row sm:items-center sm:gap-6">
              <span className="flex items-center gap-2 sm:w-72 sm:shrink-0">
                <Checkbox
                  checked={allChecked}
                  indeterminate={checkedCount > 0 && !allChecked}
                  disabled={disabled}
                  onChange={() => toggleGroup(keys)}
                />
                <span className="text-sm font-medium text-foreground">{label}</span>
              </span>
            </label>

            <ul>
              {Object.entries(actions).map(([action, desc]) => {
                const key = `${cat}.${action}`
                return (
                  <li key={key}>
                    <label className="flex cursor-pointer flex-col gap-1 py-1 pl-6 sm:flex-row sm:items-center sm:gap-6">
                      <span className="flex items-center gap-2 sm:w-72 sm:shrink-0">
                        <Checkbox
                          checked={selectedSet.has(key)}
                          disabled={disabled}
                          onChange={() => toggle(key)}
                        />
                        <span className="text-sm text-foreground">{key}</span>
                      </span>
                      <span className="text-sm text-muted-foreground">{desc}</span>
                    </label>
                  </li>
                )
              })}
            </ul>
          </section>
        )
      })}
    </div>
  )
}
